import { usePage } from '@inertiajs/react'
import { useEffect, useState } from 'react'

interface SharedFlash {
  just_connected_worker_id?: string | null
  key?: string | null
}

/**
 * A signal that a worker was just connected. Pass `undefined` (or omit) to
 * use the shared Inertia flash — the common path for credential and native
 * integration pages. Pass an explicit signal for flows where the controller
 * exposes its own page-level props (e.g. Composio, where OAuth happens in
 * another tab and the signal arrives via a dedicated page prop).
 *
 * `key` is the change detector — the hook re-fires whenever `key` changes,
 * even if `workerId` is the same. This is what lets the modal re-open when
 * the user reconnects the same worker.
 */
export interface JustConnectedSignal {
  workerId?: string | null
  key?: string | null
}

/**
 * Resolves a just-connected worker id against the provided list of workers
 * and opens a local modal state when a match is found. Intended to feed
 * `<PostConnectionModal>`.
 *
 * Default mode reads `flash.just_connected_worker_id` and `flash.key` from
 * `ApplicationController#inertia_share`. Pass an explicit signal to use
 * page-level props instead.
 */
export function useJustConnectedWorker<T extends { id: string }>(
  workers: T[],
  signal?: JustConnectedSignal,
) {
  const { flash } = usePage<{ flash: SharedFlash }>().props
  const [open, setOpen] = useState(false)
  const [worker, setWorker] = useState<T | null>(null)

  const workerId = signal !== undefined ? signal.workerId : flash?.just_connected_worker_id
  const key = signal !== undefined ? signal.key : flash?.key

  useEffect(() => {
    if (!workerId) return
    const match = workers.find((w) => String(w.id) === String(workerId))
    if (match) {
      setWorker(match)
      setOpen(true)
    }
    // Re-run when `key` changes so subsequent connections re-open the modal —
    // including reconnecting the SAME worker. `workers` is omitted to avoid
    // false re-opens when the list merely reshuffles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return {
    worker,
    open,
    close: () => setOpen(false),
  }
}
